import { positionAtSeconds, type ScorePosition, type Timeline } from '@corocanto/scoring';
import { useCallback, useEffect, useRef, useState } from 'react';
import { resumeAudio } from './context';
import { type EnginePhase, PracticeEngine, type VoiceTrack } from './engine';
import type { Channel } from './mix';
import type { Click, PlaybackPlan } from './plan';
import { loadPianoBuffers, PIANO_SAMPLES } from './samples';

export interface PlayerView {
  /** Piano samples: loading, ready or failed. */
  status: 'loading' | 'ready' | 'error';
  samplesLoaded: number;
  samplesTotal: number;
  phase: EnginePhase;
  /** Where playback is in the score (while playing). */
  position: ScorePosition | null;
  countInBeat: number;
}

/**
 * Connects the React practice page to the audio engine: loads the piano once, follows the
 * playback position on every animation frame (re-rendering only when the note position changes)
 * and writes the output level and channel gains to `diagnosticsRef` as data attributes.
 */
export function usePracticePlayer(timeline: Timeline | null) {
  const engineRef = useRef<PracticeEngine | null>(null);
  const timelineRef = useRef(timeline);
  timelineRef.current = timeline;
  const diagnosticsRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<PlayerView>({
    status: 'loading',
    samplesLoaded: 0,
    samplesTotal: PIANO_SAMPLES.length,
    phase: 'stopped',
    position: null,
    countInBeat: 0,
  });

  useEffect(() => {
    let cancelled = false;
    loadPianoBuffers((loaded, total) => {
      if (!cancelled) setView((v) => ({ ...v, samplesLoaded: loaded, samplesTotal: total }));
    })
      .then((buffers) => {
        if (cancelled) return;
        engineRef.current = new PracticeEngine(buffers);
        setView((v) => ({ ...v, status: 'ready' }));
      })
      .catch(() => {
        if (!cancelled) setView((v) => ({ ...v, status: 'error' }));
      });
    return () => {
      cancelled = true;
      engineRef.current?.dispose();
      engineRef.current = null;
    };
  }, []);

  // Follow the playback position while something is playing.
  const playing = view.phase !== 'stopped';
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    let lastKey = '';
    const tick = () => {
      const engine = engineRef.current;
      const tl = timelineRef.current;
      if (engine && tl) {
        const p = engine.position();
        const position = p.phase === 'playing' ? positionAtSeconds(tl, p.timelineSec) : null;
        const key = `${p.phase}|${p.countInBeat}|${position?.timelineMeasureIndex}|${position?.beatOffset.toFixed(2)}`;
        if (key !== lastKey) {
          lastKey = key;
          setView((v) => ({ ...v, phase: p.phase, position, countInBeat: p.countInBeat }));
        }
        const el = diagnosticsRef.current;
        if (el) el.dataset['outputLevel'] = engine.level().toFixed(3);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  const writeGains = useCallback(() => {
    const engine = engineRef.current;
    const el = diagnosticsRef.current;
    if (!engine || !el) return;
    const g = engine.gainValues();
    el.dataset['gains'] = (Object.keys(g) as Channel[]).map((ch) => `${ch}=${g[ch]}`).join(' ');
  }, []);

  const play = useCallback(
    async (
      plan: PlaybackPlan,
      options: {
        loop: boolean;
        referenceA4: number;
        onEnded?: () => void;
        voice?: VoiceTrack;
        transpose?: number;
        metronome?: readonly Click[] | null;
      },
    ): Promise<number | null> => {
      const engine = engineRef.current;
      if (!engine) return null;
      await resumeAudio();
      const startsAt = engine.play(plan, {
        ...options,
        onEnded: () => {
          setView((v) => ({ ...v, phase: 'stopped', position: null, countInBeat: 0 }));
          options.onEnded?.();
        },
      });
      setView((v) => ({ ...v, phase: plan.countIn ? 'countIn' : 'playing' }));
      return startsAt;
    },
    [],
  );

  const stop = useCallback(() => {
    engineRef.current?.stop();
    setView((v) => ({ ...v, phase: 'stopped', position: null, countInBeat: 0 }));
  }, []);

  const setGains = useCallback(
    (gains: Record<Channel, number>) => {
      engineRef.current?.setGains(gains);
      // The ramp takes 50 ms; record the target values right away for display and tests.
      const el = diagnosticsRef.current;
      if (el) el.dataset['gains'] = (Object.keys(gains) as Channel[]).map((ch) => `${ch}=${Math.round(gains[ch] * 1000) / 1000}`).join(' ');
      setTimeout(writeGains, 120);
    },
    [writeGains],
  );

  const setLoop = useCallback((loop: boolean) => engineRef.current?.setLoop(loop), []);
  const setVoiceGain = useCallback((gain: number) => engineRef.current?.setVoiceGain(gain), []);

  const playNote = useCallback(async (midi: number, durationSec: number, referenceA4: number) => {
    await resumeAudio();
    engineRef.current?.playNote(midi, durationSec, referenceA4);
  }, []);

  const playChord = useCallback(async (midis: readonly number[], durationSec: number, referenceA4: number) => {
    await resumeAudio();
    engineRef.current?.playChord(midis, durationSec, referenceA4);
  }, []);

  /** The current position, read directly (for restarts after a tempo change). */
  const currentTimelineSec = useCallback((): number | null => {
    const p = engineRef.current?.position();
    return p && p.phase !== 'stopped' ? p.timelineSec : null;
  }, []);

  return { view, play, stop, setGains, setLoop, setVoiceGain, playNote, playChord, currentTimelineSec, diagnosticsRef };
}
